import styled from '@emotion/styled';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { NotificationAccentWrap } from '@shared/components/core/alertNotification/CommonAlertNotificationStyles';
import Button from '@shared/components/core/button/CoreButton';
import IconsButton from '@shared/components/core/button/IconsButton';
import { InlineTagNodeWrap } from '@shared/components/core/card/CommonCardStyles';
import DetailContentDialog from '@shared/components/core/dialog/DetailContentDialog';
import StyledTextField from '@shared/components/core/field/StyledTextField';
import { BodySmall, LabelSmall } from '@shared/components/core/text/Typography';
import TinyHeader from '@shared/components/core/tiny/TinyHeader';
import TinyTag from '@shared/components/core/tiny/TinyTag';
import TinyValues from '@shared/components/core/tiny/TinyValues';
import {
    classifyExtensionProvenance,
    PROVENANCE_ICONS,
    provenanceLabel,
} from '@shared/constants/extensions/extensionProvenance';
import { mcpModeOptions } from '@shared/constants/extensions/mcpMode';
import {
    DEFAULT_MCP_OAUTH_LOCALHOST_PORT,
    DEFAULT_MCP_OAUTH_REDIRECT_TYPE,
    getMcpOauthRedirectUri,
    mcpOauthRedirectOptions,
} from '@shared/constants/extensions/mcpOauthRedirect';
import { getAdminAuthRedirectUri } from '@shared/constants/Urls';
import {
    DEFAULT_MCP_ACTIVATION,
    MAX_MCP_DESCRIPTION_LENGTH,
    McpActivation,
    McpAuthType,
    McpConnectionStatus,
    McpOauthRedirectType,
    McpSecretKind,
    McpServerModel,
    McpToolSummary,
    McpTransport,
} from '@shared/models/mcp/McpServerModel';
import {
    slugifyMcpName,
    validateMcpDescription,
    validateMcpName,
    validateMcpOauthLocalhostPort,
    validateTransportConfig,
} from '@shared/models/mcp/mcpValidation';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExtensionModeToggle, { IExtensionModeOption } from '../ExtensionModeToggle';
import McpApprovalRow from '../McpApprovalRow';
import McpConnectionRow from '../McpConnectionRow';
import McpToolSelector from '../McpToolSelector';
import { McpServerEditorData } from './mcpServerEditorData';

interface McpServerEditorProps {
    editorMode: 'create' | 'edit';
    /** The server to edit; omit to add a new one. */
    server?: McpServerModel;
    /** Injected persistence layer (desktop = IPC-backed; portal = REST-backed). */
    data: McpServerEditorData;
    onDone: () => void;
    onCancel: () => void;
    /** Called after a credential-stripped copy is created so the caller can switch to editing it. */
    onCopyCreated?: (newServer: McpServerModel) => void;
}

/**
 * Secret Fields: Write Only, no read access. A saved secret can never be read back, so the field holds
 * only what this session typed — `edited` is what separates "leave it alone" from "remove it".
 */
interface SecretFieldState {
    value: string;
    stored: boolean;
    edited: boolean;
}

const initSecretState = (stored?: boolean): SecretFieldState => ({
    value: '',
    stored: !!stored,
    edited: false,
});

/** Parse a `KEY=VALUE` lines textarea into a record (ignoring blank lines and lines without `=`). */
function parseEnv(text: string): Record<string, string> {
    const env: Record<string, string> = {};
    for (const line of text.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        const eq = trimmed.indexOf('=');
        if (eq <= 0) continue;
        env[trimmed.slice(0, eq).trim()] = trimmed.slice(eq + 1).trim();
    }
    return env;
}

function stringifyEnv(env?: Record<string, string>): string {
    return Object.entries(env ?? {})
        .map(([k, v]) => `${k}=${v}`)
        .join('\n');
}

/**
 * Add/edit form for an MCP "Tools" server. Covers all transports (stdio/CLI, streamable HTTP, SSE) and
 * auth schemes (none, API key, custom headers, OAuth 2.0). Secrets (API keys, OAuth client secret,
 * header values) are stored via separate encrypted IPC calls — never in the config — and are shown only
 * as "set/not set". Connecting is a single action at the bottom of the form: it saves (so the server has
 * an id and its secrets are on disk), runs OAuth sign-in when that's still outstanding, then tests the
 * connection and reads the tool list. Mirrors SkillEditor.
 */
export default function McpServerEditor({
    server,
    data,
    onCancel,
    onDone,
    editorMode,
    onCopyCreated,
}: McpServerEditorProps) {
    const { t } = useTranslation('common');
    const isEdit = !!server;
    const caps = data.capabilities;

    const [serverId, setServerId] = useState<string | null>(server?.id ?? null);
    const [name, setName] = useState(server?.name ?? '');
    const [description, setDescription] = useState(server?.description ?? '');
    const [transport, setTransport] = useState<McpTransport>(
        server?.transport ?? (caps.stdioTransport ? 'stdio' : 'http')
    );
    const [command, setCommand] = useState(server?.command ?? '');
    const [argsText, setArgsText] = useState((server?.args ?? []).join(' '));
    const [envText, setEnvText] = useState(stringifyEnv(server?.env));
    const [url, setUrl] = useState(server?.url ?? '');

    const [authType, setAuthType] = useState<McpAuthType>(server?.authType ?? 'none');
    const [apiKeyHeaderName, setApiKeyHeaderName] = useState(server?.apiKeyHeaderName ?? 'Authorization');
    const [apiKeyPrefix, setApiKeyPrefix] = useState(server?.apiKeyPrefix ?? 'Bearer ');
    const [apiKeySecret, setApiKeySecret] = useState(initSecretState(server?.hasApiKey));
    const [oauthClientId, setOauthClientId] = useState(server?.oauthClientId ?? '');
    const [oauthClientSecret, setOauthClientSecret] = useState(initSecretState(server?.hasOauthClientSecret));
    const [oauthScopes, setOauthScopes] = useState((server?.oauthScopes ?? []).join(' '));
    const [oauthDiscoveryUrl, setOauthDiscoveryUrl] = useState(server?.oauthDiscoveryUrl ?? '');
    const [oauthAuthorizationUrl, setOauthAuthorizationUrl] = useState(server?.oauthAuthorizationUrl ?? '');
    const [oauthTokenUrl, setOauthTokenUrl] = useState(server?.oauthTokenUrl ?? '');
    const [oauthRedirectType, setOauthRedirectType] = useState<McpOauthRedirectType>(
        server?.oauthRedirectType ?? DEFAULT_MCP_OAUTH_REDIRECT_TYPE
    );
    const [oauthLocalhostPort, setOauthLocalhostPort] = useState<number>(
        server?.oauthLocalhostPort ?? DEFAULT_MCP_OAUTH_LOCALHOST_PORT
    );
    const oauthRedirectUri = getMcpOauthRedirectUri(oauthRedirectType, oauthLocalhostPort);

    const [activation, setActivation] = useState<McpActivation>(server?.activation ?? DEFAULT_MCP_ACTIVATION);
    const [requireApproval, setRequireApproval] = useState(server?.requireApproval ?? true);
    const canEdit = server ? classifyExtensionProvenance(server) === 'mine' : true;

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [busyAction, setBusyAction] = useState<'test' | 'oauth' | null>(null);
    const [testResult, setTestResult] = useState<{
        status: McpConnectionStatus;
        tools: McpToolSummary[];
        error?: string;
    } | null>(server ? { status: server.connectionStatus, tools: server.cachedTools } : null);
    const [oauthConnected, setOauthConnected] = useState(!!server?.oauthConnected);
    const [redirectCopied, setRedirectCopied] = useState(false);

    // Per-tool selection: which of the server's discovered tools are turned OFF (bare names), plus the
    // model's context-window size so we can show what fraction of it the enabled tools will consume.
    const [disabledTools, setDisabledTools] = useState<string[]>(server?.disabledTools ?? []);
    const [contextWindow, setContextWindow] = useState<number>(0);

    useEffect(() => {
        if (!data.getContextWindow) return;
        data.getContextWindow()
            .then(setContextWindow)
            .catch(() => setContextWindow(0));
    }, []);

    const nameError = useMemo(() => (name.trim() ? validateMcpName(name).error : undefined), [name]);
    const descriptionError = useMemo(
        () => (description.trim() ? validateMcpDescription(description).error : undefined),
        [description]
    );
    const transportError = useMemo(
        () => validateTransportConfig({ transport, command, url }).error,
        [transport, command, url]
    );
    const redirectOptions = useMemo(() => mcpOauthRedirectOptions(t), [t]);
    const redirectPortError =
        oauthRedirectType === 'localhost' ? validateMcpOauthLocalhostPort(oauthLocalhostPort).error : undefined;
    const canSave =
        !!name.trim() &&
        !nameError &&
        !!description.trim() &&
        !descriptionError &&
        !transportError &&
        !redirectPortError &&
        !saving;

    /**
     * The form's non-secret fields. `commitToolSelection` is false for the writes the user didn't ask for
     * (test connection, OAuth sign-in): those have to persist the connection config to get an id and its
     * secrets, but the tool selection must reach disk — and therefore a run — only on save. Leaving the
     * field undefined is what makes the update skip it.
     */
    const buildBaseInput = (commitToolSelection: boolean) => ({
        name: name.trim(),
        description: description.trim(),
        transport,
        command: transport === 'stdio' ? command.trim() : undefined,
        args: transport === 'stdio' ? argsText.split(/\s+/).filter(Boolean) : undefined,
        env: transport === 'stdio' ? parseEnv(envText) : undefined,
        url: transport !== 'stdio' ? url.trim() : undefined,
        authType: transport === 'stdio' ? ('none' as McpAuthType) : authType,
        apiKeyHeaderName,
        apiKeyPrefix,
        // reset on cleared value
        oauthClientId: isEdit ? oauthClientId.trim() : oauthClientId.trim() || undefined,
        oauthScopes: oauthScopes.split(/\s+/).filter(Boolean),
        oauthDiscoveryUrl: oauthDiscoveryUrl.trim() || undefined,
        oauthAuthorizationUrl: oauthAuthorizationUrl.trim() || undefined,
        oauthTokenUrl: oauthTokenUrl.trim() || undefined,
        oauthRedirectType,
        // Test connection saves without waiting for `canSave`, so a rejected port must not reach disk —
        // omitting it leaves the last good value stored rather than writing one nothing can bind.
        oauthLocalhostPort: redirectPortError ? undefined : oauthLocalhostPort,
        activation,
        requireApproval,
        disabledTools: commitToolSelection ? disabledTools : undefined,
    });

    /** Persist the current form (create on first save, then update), returning the server id. */
    const persist = async (commitToolSelection: boolean): Promise<string> => {
        if (serverId) {
            await data.updateServer({ id: serverId, ...buildBaseInput(commitToolSelection) });
            return serverId;
        }
        const created = await data.createServer(buildBaseInput(commitToolSelection));
        setServerId(created.id);
        return created.id;
    };

    const commitSecret = async (
        id: string,
        kind: McpSecretKind,
        { value, stored, edited }: SecretFieldState,
        setState: (next: SecretFieldState) => void
    ) => {
        // An untouched field is empty because the secret is unreadable, not because it was removed.
        if (!edited || !data.setSecret) return;
        if (value) {
            await data.setSecret(id, kind, value);
            setState({ value, stored: true, edited: false });
        } else if (stored && data.clearSecret) {
            await data.clearSecret(id, kind);
            setState({ value: '', stored: false, edited: false });
        }
    };

    /** Persist current form + any freshly-entered secrets. Returns the id. */
    const persistWithSecrets = async (commitToolSelection: boolean): Promise<string> => {
        const id = await persist(commitToolSelection);
        // Secrets are only handled on platforms with an on-device encrypted store (desktop).
        if (caps.secrets && data.setSecret && transport !== 'stdio') {
            if (authType === 'apiKey') await commitSecret(id, 'apiKey', apiKeySecret, setApiKeySecret);
            if (authType === 'oauth') {
                await commitSecret(id, 'oauthClientSecret', oauthClientSecret, setOauthClientSecret);
            }
        }
        return id;
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            await persistWithSecrets(true);
            onDone();
        } catch (e) {
            setSaveError(e instanceof Error ? e.message : t('mcp.editor.saveError'));
        } finally {
            setSaving(false);
        }
    };

    /** True when this server authenticates with OAuth and this platform can run the sign-in flow. */
    const usesOauth = transport !== 'stdio' && authType === 'oauth' && caps.oauth;
    /** An OAuth server that hasn't been authorized yet — testing it has to sign in first. */
    const needsOauth = usesOauth && !oauthConnected && !!data.oauthConnect;

    /**
     * One action for "make this server work": save the form, sign in if OAuth hasn't happened yet, then
     * connect and read the tool list. Splitting the sign-in into its own button only ever meant the test
     * failed for anyone who pressed them out of order.
     */
    const handleTest = async () => {
        setBusyAction(needsOauth ? 'oauth' : 'test');
        setSaveError(null);
        try {
            const id = await persistWithSecrets(false);
            if (needsOauth && data.oauthConnect) {
                const updated = await data.oauthConnect(id);
                setOauthConnected(!!updated.oauthConnected);
            }
            if (!data.testConnection) return;
            setBusyAction('test');
            const result = await data.testConnection(id);
            setTestResult(result);
            // A tool the server no longer advertises can't be turned off — drop it, or saving the form
            // would write the stale name back over the pruning the test connection just did.
            if (result.status === 'ok') {
                const live = new Set(result.tools.map((tool) => tool.name));
                setDisabledTools((current) => current.filter((name) => live.has(name)));
            }
        } catch (e) {
            setSaveError(e instanceof Error ? e.message : t('mcp.status.error'));
        } finally {
            setBusyAction(null);
        }
    };

    /** The tools to show for selection — the last successful test's manifest (seeded from the server). */
    const tools: McpToolSummary[] = testResult?.status === 'ok' ? testResult.tools : (server?.cachedTools ?? []);

    const handleOauthConnect = async () => {
        setBusyAction('oauth');
        setSaveError(null);
        try {
            const id = await persistWithSecrets(false);
            if (!data.oauthConnect) return;
            const updated = await data.oauthConnect(id);
            setOauthConnected(!!updated.oauthConnected);
        } catch (e) {
            setSaveError(e instanceof Error ? e.message : t('mcp.oauth.error'));
        } finally {
            setBusyAction(null);
        }
    };

    const handleOauthDisconnect = async () => {
        if (!serverId || !data.oauthDisconnect) return;
        await data.oauthDisconnect(serverId);
        setOauthConnected(false);
        // The last test's result no longer applies once we've signed out, and the row reads its status
        // from it — drop it so the status falls back to "unknown" instead of a stale "connected".
        setTestResult(null);
    };

    /** Copy the server's non-secret config into a new user-owned server, then hand it to the caller. */
    const handleCopyServer = async () => {
        setSaveError(null);
        try {
            const cleansedName = slugifyMcpName(name.trim()) || name.trim();
            const copyName = t('mcp.editor.copyName', { name: cleansedName });
            const newServer = await data.createServer({
                ...buildBaseInput(true),
                name: copyName,
                // Credentials are never copied — the user must supply their own.
                oauthClientId: undefined,
                env: undefined,
            });
            onCopyCreated?.(newServer);
        } catch (e) {
            setSaveError(e instanceof Error ? e.message : t('mcp.editor.saveError'));
        }
    };

    const transportOptions: { value: McpTransport; label: string }[] = [
        // stdio (local process) is only offered on platforms that can spawn one.
        ...(caps.stdioTransport ? [{ value: 'stdio' as McpTransport, label: t('mcp.transport.stdio') }] : []),
        { value: 'http', label: t('mcp.transport.http') },
        { value: 'sse', label: t('mcp.transport.sse') },
    ];

    /**
     * The card and the detail dialog offer Off/Auto/Manual; the editor offers the two activations only —
     * pausing a server is something you do to one that already exists, not a choice made while writing it.
     */
    const activationOptions: IExtensionModeOption<McpActivation>[] = mcpModeOptions(t)
        .filter((option) => option.value !== 'off')
        .map((option) => ({ ...option, value: option.value as McpActivation }));

    const authOptions: { value: McpAuthType; label: string }[] = [
        { value: 'none', label: t('mcp.auth.none') },
        { value: 'apiKey', label: t('mcp.auth.apiKey') },
        // { value: 'headers', label: t('mcp.auth.headers') },
        { value: 'oauth', label: t('mcp.auth.oauth') },
    ];

    /**
     * A write-only secret field (API key / OAuth client secret). What the user types is obscured by the
     * password input; a secret already on disk has nothing to show, so the placeholder stands in for it.
     */
    const secretField = (label: string, state: SecretFieldState, setState: (next: SecretFieldState) => void) => (
        <StyledTextField
            fullWidth
            size="small"
            type="password"
            label={label}
            value={state.value}
            placeholder={state.stored && !state.edited ? t('mcp.editor.secretSet') : undefined}
            onChange={(e) => setState({ ...state, value: e.target.value, edited: true })}
            disabled={saving}
            slotProps={{ inputLabel: { shrink: true } }}
        />
    );

    const credentialsEditor = () => {
        return (
            <>
                {transport === 'stdio' ? (
                    <>
                        <Field>
                            <StyledTextField
                                fullWidth
                                size="small"
                                label={t('mcp.editor.commandLabel')}
                                value={command}
                                placeholder="npx"
                                onChange={(e) => setCommand(e.target.value)}
                                error={!!transportError}
                                helperText={transportError}
                                disabled={saving}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Field>
                        <Field>
                            <StyledTextField
                                fullWidth
                                size="small"
                                label={t('mcp.editor.argsLabel')}
                                value={argsText}
                                placeholder="-y @modelcontextprotocol/server-filesystem /path"
                                onChange={(e) => setArgsText(e.target.value)}
                                helperText={t('mcp.editor.argsHelp')}
                                disabled={saving}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Field>
                        <Field>
                            <StyledTextField
                                label={t('mcp.editor.envLabel')}
                                fullWidth
                                multiline
                                minRows={2}
                                value={envText}
                                placeholder={'KEY=value\nANOTHER_KEY=value'}
                                onChange={(e) => setEnvText(e.target.value)}
                                helperText={t('mcp.editor.envHelp')}
                                disabled={saving}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Field>
                    </>
                ) : (
                    <>
                        <Field>
                            <StyledTextField
                                fullWidth
                                size="small"
                                label={t('mcp.editor.urlLabel')}
                                value={url}
                                placeholder="https://example.com/mcp"
                                onChange={(e) => setUrl(e.target.value)}
                                error={!!transportError}
                                helperText={transportError}
                                disabled={saving}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Field>

                        <Field>
                            <TinyHeader size="small" title={t('mcp.editor.authLabel')} />
                            <SegmentRow role="radiogroup">
                                {authOptions.map((option) => (
                                    <Segment
                                        key={option.value}
                                        type="button"
                                        role="radio"
                                        aria-checked={authType === option.value}
                                        $selected={authType === option.value}
                                        disabled={saving}
                                        onClick={() => setAuthType(option.value)}>
                                        {option.label}
                                    </Segment>
                                ))}
                            </SegmentRow>
                        </Field>

                        {authType === 'apiKey' && (
                            <>
                                <TwoCol>
                                    <Field>
                                        <StyledTextField
                                            fullWidth
                                            size="small"
                                            label={t('mcp.editor.apiKeyHeaderLabel')}
                                            value={apiKeyHeaderName}
                                            onChange={(e) => setApiKeyHeaderName(e.target.value)}
                                            disabled={saving}
                                            slotProps={{ inputLabel: { shrink: true } }}
                                        />
                                    </Field>
                                    <Field>
                                        <StyledTextField
                                            fullWidth
                                            size="small"
                                            label={t('mcp.editor.apiKeyPrefixLabel')}
                                            value={apiKeyPrefix}
                                            onChange={(e) => setApiKeyPrefix(e.target.value)}
                                            disabled={saving}
                                            slotProps={{ inputLabel: { shrink: true } }}
                                        />
                                    </Field>
                                </TwoCol>
                                {caps.secrets ? (
                                    <Field>
                                        {secretField(t('mcp.editor.apiKeyLabel'), apiKeySecret, setApiKeySecret)}
                                    </Field>
                                ) : (
                                    <FieldHint>{t('mcp.editor.secretsOnDevice')}</FieldHint>
                                )}
                            </>
                        )}

                        {authType === 'oauth' && (
                            <>
                                {caps.oauth && (
                                    <Banner>
                                        <TinyHeader
                                            size="small"
                                            title={t('mcp.editor.redirectLabel')}
                                            description={t('mcp.editor.redirectHelp')}
                                        />
                                        <TwoCol>
                                            <Field>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel id="mcp-redirect-url">
                                                        {t('mcp.editor.redirectTypeLabel')}
                                                    </InputLabel>
                                                    <Select
                                                        labelId="mcp-redirect-url"
                                                        label={t('mcp.editor.redirectTypeLabel')}
                                                        value={oauthRedirectType}
                                                        disabled={saving}
                                                        onChange={(e) => {
                                                            setOauthRedirectType(
                                                                e.target.value as McpOauthRedirectType
                                                            );
                                                            setRedirectCopied(false);
                                                        }}>
                                                        {redirectOptions.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Field>
                                            {oauthRedirectType === 'localhost' && (
                                                <Field>
                                                    <StyledTextField
                                                        fullWidth
                                                        size="small"
                                                        type="number"
                                                        label={t('mcp.editor.redirectPortLabel')}
                                                        value={oauthLocalhostPort}
                                                        onChange={(e) => {
                                                            setOauthLocalhostPort(Number(e.target.value));
                                                            setRedirectCopied(false);
                                                        }}
                                                        error={!!redirectPortError}
                                                        helperText={redirectPortError}
                                                        disabled={saving}
                                                        slotProps={{
                                                            inputLabel: { shrink: true },
                                                            htmlInput: { min: 1024, max: 65535 },
                                                        }}
                                                    />
                                                </Field>
                                            )}
                                        </TwoCol>
                                        <RedirectRow>
                                            <RedirectValue>{oauthRedirectUri}</RedirectValue>
                                            <IconsButton
                                                label={t('copy')}
                                                startIcon="duplicate"
                                                $color="simple"
                                                hideBorder
                                                onClick={() => {
                                                    navigator.clipboard?.writeText(oauthRedirectUri);
                                                    setRedirectCopied(true);
                                                }}
                                            />
                                            {redirectCopied && <CopiedText>{t('copied')}</CopiedText>}
                                        </RedirectRow>
                                        <FieldHint>{t(`mcp.editor.redirectNotes.${oauthRedirectType}`)}</FieldHint>
                                    </Banner>
                                )}
                                <TwoCol>
                                    <Field>
                                        <StyledTextField
                                            fullWidth
                                            size="small"
                                            label={t('mcp.editor.oauthClientIdLabel')}
                                            value={oauthClientId}
                                            onChange={(e) => setOauthClientId(e.target.value)}
                                            helperText={t('mcp.editor.oauthClientIdHelp')}
                                            disabled={saving}
                                            slotProps={{ inputLabel: { shrink: true } }}
                                        />
                                    </Field>
                                    {caps.secrets && (
                                        <Field>
                                            {secretField(
                                                t('mcp.editor.oauthClientSecretLabel'),
                                                oauthClientSecret,
                                                setOauthClientSecret
                                            )}
                                        </Field>
                                    )}
                                </TwoCol>
                                <Field>
                                    <StyledTextField
                                        fullWidth
                                        size="small"
                                        label={t('mcp.editor.oauthScopesLabel')}
                                        value={oauthScopes}
                                        placeholder="read write"
                                        onChange={(e) => setOauthScopes(e.target.value)}
                                        helperText={t('mcp.editor.oauthScopesHelp')}
                                        disabled={saving}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                    />
                                </Field>
                                <Field>
                                    <StyledTextField
                                        fullWidth
                                        size="small"
                                        label={t('mcp.editor.oauthDiscoveryLabelShort')}
                                        value={oauthDiscoveryUrl}
                                        placeholder="https://example.com/.well-known/oauth-authorization-server"
                                        // "(optional)" in the label bloats it and isn't a convention MUI has;
                                        // the helper is where a field says what happens if you skip it.
                                        helperText={t('mcp.editor.oauthDiscoveryHelp')}
                                        onChange={(e) => setOauthDiscoveryUrl(e.target.value)}
                                        disabled={saving}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                    />
                                </Field>
                                <TwoCol>
                                    <Field>
                                        <StyledTextField
                                            fullWidth
                                            size="small"
                                            label={t('mcp.editor.oauthAuthUrlLabel')}
                                            value={oauthAuthorizationUrl}
                                            onChange={(e) => setOauthAuthorizationUrl(e.target.value)}
                                            disabled={saving}
                                            slotProps={{
                                                inputLabel: {
                                                    shrink: true,
                                                },
                                            }}
                                        />
                                    </Field>
                                    <Field>
                                        <StyledTextField
                                            fullWidth
                                            size="small"
                                            label={t('mcp.editor.oauthTokenUrlLabel')}
                                            value={oauthTokenUrl}
                                            onChange={(e) => setOauthTokenUrl(e.target.value)}
                                            disabled={saving}
                                            slotProps={{ inputLabel: { shrink: true } }}
                                        />
                                    </Field>
                                </TwoCol>
                                {/* Signing in happens as part of Test connection at the bottom of the editor,
                                so there's one button for "make this server work" rather than two that
                                have to be pressed in the right order. */}
                                {!caps.oauth && <FieldHint>{t('mcp.editor.oauthOnDevice')}</FieldHint>}
                            </>
                        )}

                        {authType === 'headers' && <FieldHint>{t('mcp.editor.headersHelp')}</FieldHint>}
                    </>
                )}
            </>
        );
    };

    const editingBanner = () => {
        if (!server) return null;
        const classification = classifyExtensionProvenance(server);
        if (classification === 'mine') return null;

        const provenance = provenanceLabel(classification, t);

        return (
            <Banner>
                <InlineTagNodeWrap>
                    <TinyTag
                        title={t('extensions.managedBy', { provenance })}
                        variant="accent"
                        icon={PROVENANCE_ICONS[classification]}
                    />
                </InlineTagNodeWrap>
                <InlineRow>
                    <StyledLabel>{server.name}</StyledLabel>·
                    <StyledLabel>{t(`extensions.integrationType.${classification}`)}</StyledLabel>
                </InlineRow>
                <BodySmall>{t(`mcp.editor.noPermissions`, { provenance })}</BodySmall>
                <InlineTagNodeWrap>
                    {classification === 'organization' ? (
                        <Button target="_blank" $size="small" href={getAdminAuthRedirectUri()} $color="secondary">
                            {t('mcp.editor.altOptions.organization')}
                        </Button>
                    ) : (
                        <Button onClick={handleCopyServer} disabled={saving} $size="small" $color="secondary">
                            {t('mcp.editor.altOptions.copy')}
                        </Button>
                    )}
                </InlineTagNodeWrap>
            </Banner>
        );
    };

    return (
        <DetailContentDialog
            open
            fullWidth
            maxWidth="md"
            scroll="paper"
            onClose={onCancel}
            onCancel={onCancel}
            onConfirm={handleSave}
            confirmDisabled={!canSave}
            cancelButtonText={t('cancel')}
            confirmButtonText={isEdit ? t('mcp.editor.save') : t('mcp.editor.create')}
            confirmIcon="save"
            error={saveError ?? undefined}
            title={editorMode === 'edit' ? t('mcp.editTitle') : t('mcp.newTitle')}>
            <Body>
                <Field>
                    <StyledTextField
                        fullWidth
                        size="small"
                        label={t('mcp.editor.nameLabel')}
                        value={name}
                        placeholder={t('mcp.editor.namePlaceholder')}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={() => setName((current) => slugifyMcpName(current) || current)}
                        error={!!nameError}
                        helperText={nameError ?? t('mcp.editor.nameHelp')}
                        disabled={saving}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                </Field>

                <Field>
                    <StyledTextField
                        label={t('mcp.editor.descriptionLabel')}
                        fullWidth
                        multiline
                        minRows={2}
                        value={description}
                        placeholder={t('mcp.editor.descriptionPlaceholder')}
                        onChange={(e) => setDescription(e.target.value.slice(0, MAX_MCP_DESCRIPTION_LENGTH))}
                        error={!!descriptionError}
                        helperText={descriptionError ?? t('mcp.editor.descriptionHelp')}
                        disabled={saving}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                </Field>

                <Field>
                    <TinyHeader size="small" title={t('mcp.editor.transportLabel')} />
                    <SegmentRow role="radiogroup">
                        {transportOptions.map((option) => (
                            <Segment
                                key={option.value}
                                type="button"
                                role="radio"
                                aria-checked={transport === option.value}
                                $selected={transport === option.value}
                                disabled={saving}
                                onClick={() => setTransport(option.value)}>
                                {option.label}
                            </Segment>
                        ))}
                    </SegmentRow>
                </Field>

                {canEdit ? credentialsEditor() : editingBanner()}

                {/* The same three controls the detail dialog shows, from the same components — a server's mode,
                its approval setting, and its connection shouldn't look or behave differently depending on
                whether the user got here from the card or from Edit. Off isn't offered while editing: it's
                a "pause this" action, and the card and dialog are where you'd reach for it. */}
                <Field>
                    <ExtensionModeToggle<McpActivation>
                        label={t('mcp.editor.activationLabel')}
                        options={activationOptions}
                        value={activation}
                        onChange={setActivation}
                    />
                </Field>

                <McpApprovalRow requireApproval={requireApproval} disabled={saving} onToggle={setRequireApproval} />

                {caps.testConnection && (
                    <Field>
                        <McpConnectionRow
                            status={testResult?.status ?? 'unknown'}
                            error={testResult?.error}
                            busy={busyAction}
                            disabled={saving}
                            needsOauth={needsOauth}
                            canDisconnect={usesOauth && oauthConnected}
                            onTest={() => handleTest()}
                            onReconnect={handleOauthConnect}
                            onDisconnect={handleOauthDisconnect}>
                            {testResult?.status === 'ok' && (
                                <TinyValues
                                    value={testResult.tools.length}
                                    title={t('mcp.tools.label')}
                                    icon="listChecks"
                                />
                            )}
                        </McpConnectionRow>
                    </Field>
                )}

                {caps.toolSelection && (
                    <Field>
                        <McpToolSelector
                            tools={tools}
                            disabledTools={disabledTools}
                            contextWindow={contextWindow}
                            disabled={saving}
                            onChange={setDisabledTools}
                        />
                    </Field>
                )}
            </Body>
        </DetailContentDialog>
    );
}

/**
 * The gap between fields is deliberately wider than the gap inside one (label → input → helper): a
 * helper has to sit closer to the field it describes than to the next field's label, or it reads as
 * loose body copy between the two.
 */
const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding-top: 0.5rem;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
`;

const TwoCol = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 12rem), 1fr));
    gap: 0.75rem;
`;

const FieldHint = styled(BodySmall)`
    color: ${(props) => props.theme.color.textBody};
`;

const Banner = styled(NotificationAccentWrap)`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.625rem 0.75rem;
    border-radius: 0.5rem;
`;

const RedirectRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const RedirectValue = styled.code`
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    white-space: nowrap;
    padding: 0.3rem 0.5rem;
    border-radius: 0.375rem;
    background: ${(props) => props.theme.color.background};
    border: 1px solid ${(props) => props.theme.color.border};
    font-size: 0.75rem;
    font-family: monospace;
    color: ${(props) => props.theme.color.text};
`;

const CopiedText = styled(BodySmall)`
    color: ${(props) => props.theme.color.success};
    flex: none;
`;

const SegmentRow = styled.div`
    display: flex;
    border: 1px solid ${(props) => props.theme.color.border};
    border-radius: 0.5rem;
    overflow: hidden;
`;

const Segment = styled.button<{ $selected?: boolean }>`
    flex: 1;
    padding: 0.4rem 0.5rem;
    font-size: 0.8125rem;
    font-weight: ${(props) => (props.$selected ? 600 : 400)};
    cursor: pointer;
    border: none;
    background: ${(props) => (props.$selected ? props.theme.color.accent : 'transparent')};
    color: ${(props) => (props.$selected ? props.theme.color.background : props.theme.color.textBody)};

    & + & {
        border-left: 1px solid ${(props) => props.theme.color.border};
    }
    &:hover {
        background: ${(props) => (props.$selected ? props.theme.color.accent : props.theme.color.cardHover)};
        color: ${(props) => (props.$selected ? props.theme.color.background : props.theme.color.text)};
    }
`;

const InlineRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const StyledLabel = styled(LabelSmall)`
    color: ${(props) => props.theme.banner['accent'].text};
`;
